/**
 * useCRMDubletten Hook — Top-20-Dubletten-Kandidaten (Issue #384).
 *
 * Liest aus dem Admin-Wrapper-View `crm_kontakt_dubletten_admin` (Migration #376).
 * Der View ist über RLS-Substitute auf admin-Rolle beschränkt — Mitarbeiter
 * sehen leere Ergebnisse, deshalb ist die Page selbst zusätzlich per
 * ModuleGuard moduleId='crm' abgesichert.
 *
 * Da die View nur Kontakt-IDs liefert, holen wir die Namen + Firma in einem
 * zweiten Roundtrip aus `crm_kontakte` und joinen im Frontend.
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import {
  buildDublettenRows,
  topNByScore,
  type DublettenKandidat,
  type DublettenReportRow,
  type CRMKontaktInfo,
  type MatchReason,
} from '@/lib/dubletten-report';

export const TOP_N = 20;

export function useCRMDubletten() {
  const [rows, setRows] = useState<DublettenReportRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const { data: kandidaten, error: kErr } = await supabase
        .from('crm_kontakt_dubletten_admin')
        .select('kontakt_a_id,kontakt_b_id,match_reason,score,erkannt_am')
        .order('score', { ascending: false })
        .limit(TOP_N);
      if (kErr) throw kErr;

      const kandidatenList = (kandidaten ?? []) as DublettenKandidat[];
      const top = topNByScore(
        kandidatenList.filter((k) => isMatchReason(k.match_reason)),
        TOP_N,
      );

      if (top.length === 0) {
        setRows([]);
        setError(null);
        return;
      }

      const ids = Array.from(
        new Set(top.flatMap((k) => [k.kontakt_a_id, k.kontakt_b_id])),
      );
      const { data: kontakte, error: cErr } = await supabase
        .from('crm_kontakte')
        .select('id,name,firma')
        .in('id', ids);
      if (cErr) throw cErr;

      const built = buildDublettenRows(top, (kontakte ?? []) as CRMKontaktInfo[]);
      setRows(built);
      setError(null);
    } catch (e: any) {
      console.error('CRM Dubletten fetch error:', e);
      setError(e?.message ?? 'Unbekannter Fehler');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { rows, loading, error, refetch: fetch };
}

function isMatchReason(v: unknown): v is MatchReason {
  return v === 'email' || v === 'phone' || v === 'fuzzy';
}
